import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMGdvbGQlMjBidXNpbmVzcyUyMGFic3RyYWN0JTIwYmFja2dyb3VuZCUyMGFyY2hpdGVjdHVyZSUyMGV4ZWN1dGl2ZSUyMGZpbmFuY2lhbCUyMHdlYWx0aHxlbnwwfHx8fDE3ODk4NDI5NDJ8MA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-2 -mb-2">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const STATS = [
  { value: "500+", label: "严选入库项目" },
  { value: "12,000+", label: "注册会员" },
  { value: "¥128", label: "每邀请1人奖励" },
  { value: "99%", label: "劣质项目淘汰率" },
];

export default function Hero() {
  const { openAuth, user } = useAuth();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grain">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="" className="w-full h-[125%] object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070709]/70 via-[#070709]/50 to-[#070709]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 pt-28 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-[#D4AF37]/60" />
          <span className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37]">
            HEYING PROJECT EXCHANGE COMMUNITY
          </span>
        </motion.div>

        <h1 data-testid="hero-title" className="font-serif-sc font-black leading-[1.08] text-5xl sm:text-6xl lg:text-8xl tracking-tight">
          <MaskedLine delay={0.25}>汇聚项目资源</MaskedLine>
          <MaskedLine delay={0.4} className="text-gold-grad">连接可靠项目</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-gray-400 leading-relaxed"
        >
          合赢项目交流社区 · 致力于为成员提供可靠、零风险的赚钱项目。
          四重专业体系层层把关，只交付经得起验证的机会。
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          {!user && (
            <button
              data-testid="hero-card-key-btn"
              onClick={() => openAuth()}
              className="btn-gold px-8 py-3.5 text-sm inline-flex items-center gap-2"
            >
              <KeyRound className="w-4 h-4" />
              免费注册 · 立即加入
            </button>
          )}
          <a href="#values" data-testid="hero-explore-btn" className="btn-ghost-gold px-8 py-3.5 text-sm inline-block">
            探索核心价值
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-[#D4AF37]/15"
        >
          {STATS.map((s, i) => (
            <div key={s.label} className={`py-6 pr-6 ${i > 0 ? "lg:border-l lg:border-[#D4AF37]/15 lg:pl-6" : ""}`}>
              <p className="font-mono-num text-2xl lg:text-3xl text-[#D4AF37]">{s.value}</p>
              <p className="mt-1 text-xs tracking-widest text-gray-500">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
      >
        <ChevronDown className="w-5 h-5 text-[#D4AF37]/60 animate-bounce" />
      </motion.div>
    </section>
  );
}
